import {
  Button,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';

import { useNavigate } from 'react-router-dom';
import { useMutation } from 'urql';
import { useTranslation } from 'react-i18next';
import {
  DuplicateOrderDocument,
  EventResource,
  EventType,
  OrderDeepFragment,
} from '../../gql/graphql';
import { useEventHistory } from 'src/hooks/event-history';

type CopyOrderFormData = {
  name: string;
};

interface CopyOrderModalProps {
  order: OrderDeepFragment;
  modalIsOpen: boolean;
  closeModal: () => void;
}

export default function CopyOrderModal({
  order,
  modalIsOpen,
  closeModal,
}: CopyOrderModalProps) {
  const { register, handleSubmit, reset } = useForm<CopyOrderFormData>();
  const navigate = useNavigate();

  const { t } = useTranslation('project');

  const toast = useToast();
  const [{ fetching }, duplicateOrder] = useMutation(DuplicateOrderDocument);

  const { logEvent } = useEventHistory(order.projectId);

  const onSubmit = handleSubmit(async ({ name }) => {
    const { data, error } = await duplicateOrder({
      input: {
        newOrderName: name,
        theOrderId: order.id,
      },
    });

    if (error) {
      toast({
        title: t('toast.error.duplicating-table'),
        status: 'error',
      });
      throw error;
    }
    if (data?.duplicateOrder?.order?.id) {
      const createdOrderId = data?.duplicateOrder?.order?.id;
      closeModal();
      reset();
      navigate(`/project/${order.projectId}/takeoff/${createdOrderId}`);

      logEvent({
        eventName: 'Table Duplicated',
        eventType: EventType.Created,
        resourceName: name,
        resourceType: EventResource.Order,
        metadata: {
          affectedResources: [createdOrderId],
          details: `Duplicated table ${order.name}`,
        },
      });
    }
  });

  return (
    <Modal isOpen={modalIsOpen} onClose={closeModal} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('duplicate-table-modal.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form onSubmit={onSubmit} id="copy-order-form">
            <FormControl isRequired>
              <Input
                autoFocus
                {...register('name')}
                placeholder={t('duplicate-table-modal.name-placeholder')}
              />
            </FormControl>
          </form>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button
            isLoading={fetching}
            colorScheme="brand"
            type="submit"
            form="copy-order-form"
          >
            {t('duplicate-table-modal.button.duplicate')}
          </Button>
          <Button colorScheme="gray" onClick={closeModal}>
            {t('duplicate-table-modal.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
